import React from "react";
import { Divider, Stack, Typography } from "@mui/material";
import { startOfWeek, endOfWeek, format } from "date-fns";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";

/**
 * @component
 * @description `CustomChartTooltip` component to display the custom tooltip for the chart.
 * @param {Object} props
 * @param {Object} props.config - The chart config
 * @param {Object} props.tooltip - The tooltip object
 * @param {string?} props.subTitle - The subtitle to display in the tooltip
 * @returns {JSX.Element} The CustomChartTooltip component
 */
export default function CustomChartTooltip({ config, tooltip, subTitle }) {
  if (!config || !tooltip) {
    return null; // No tooltip data available
  }

  const dataIndex = tooltip?.dataPoints ? tooltip.dataPoints[0]?.dataIndex : 0;
  const dataSets = config?._config?.data?.datasets || [];

  const dates = dataSets[0]?.dates;
  const period = dataSets[0]?.period;
  const totalDuration = dataSets[0]?.totalDuration;

  let dateRange = "No date range available";

  if (Array.isArray(dates) && period) {
    const periodDate = dateWithoutTimezone(dates[dataIndex]);

    if (period === "weeks") {
      const weekStart = startOfWeek(periodDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(periodDate, { weekStartsOn: 1 });
      dateRange = `${format(weekStart, "MMM do")} thru ${format(weekEnd, "MMM do")}`;
    } else if (period === "months") {
      dateRange = format(periodDate, "MMMM");
    }
  }

  return (
    <Stack px={4} py={3} gap={3}>
      <Typography component="p" variant="chartTooltip">
        {dateRange}
      </Typography>

      <Divider />

      {subTitle && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography component="p" variant="chartTooltip">
            {subTitle}
          </Typography>
          <Typography component="p" variant="chartTooltip" fontWeight={500}>
            {totalDuration[dataIndex]}
          </Typography>
        </Stack>
      )}

      {dataSets.map((dataset, index) => {
        const dataValue = dataset.data[dataIndex];

        return (
          <Stack key={index} direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={1} alignItems="center">
              <span
                style={{
                  backgroundColor: dataset.backgroundColor,
                  width: "8px",
                  height: "8px",
                  borderRadius: "4px",
                }}
              />
              <Typography component="span" variant="chartTooltip">
                {dataset.label}
              </Typography>
            </Stack>

            <Typography component="p" variant="chartTooltip">
              {dataValue ?? 0}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}
